import { graphql } from 'react-relay';
import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import {
	InitialPermissionOptions,
	type useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation as useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$data,
	type useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutationVariables as useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$variables,
} from './__types__/useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation.ts';
import type {
	useCreatePersonalSpaceGetPublicNameQuery as useCreatePersonalSpaceGetPublicNameQuery$data,
	useCreatePersonalSpaceGetPublicNameQueryVariables as useCreatePersonalSpaceGetPublicNameQuery$variables,
} from './__types__/useCreatePersonalSpaceGetPublicNameQuery.ts';
import type { useCreatePersonalSpacepersonalSpaceKeyQuery as useCreatePersonalSpacepersonalSpaceKeyQuery$data } from './__types__/useCreatePersonalSpacepersonalSpaceKeyQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import type { ApolloError } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import { expValEquals } from '@confluence/feature-experiments';
// Defined at module scope so the same GraphQL tagged node can be reused both for the
// initial useQueryInterop fetch and for refetchQueries on the createPersonalSpace mutation.
// (Relay disallows defining the same named query twice, so we must share one node.)
const personalSpaceKeyQuery = graphql`
	query useCreatePersonalSpacepersonalSpaceKeyQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

const isPersonalSpaceExistsError = (error: ApolloErrorInterop) =>
	/A personal space already exists for this user/.test(error?.message);

export enum CreationSource {
	NTH_USER_ACCOUNT_SETUP = 'nthUserAccountSetup',
	ADMIN_ACCOUNT_SETUP = 'adminAccountSetup',
	ADMIN_STO_V2 = 'adminStoV2AccountSetup',
}

interface UseCreatePersonalSpaceProps {
	source: CreationSource;
	hasPublicSpace?: boolean;
	isFoundingAdmin?: boolean;
}

export const useCreatePersonalSpace = ({
	source,
	hasPublicSpace,
	isFoundingAdmin,
}: UseCreatePersonalSpaceProps): {
	createPersonalSpace: () =>
		| Promise<void>
		| Promise<{
				data?: useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$data | undefined;
		  }>;
	queryLoading: boolean;
	mutationLoading: boolean;
	error: ApolloError | undefined;
	personalSpaceData: useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$data | undefined;
	createPersonalSpaceCalled: boolean;
	personalSpaceKeyData: useCreatePersonalSpacepersonalSpaceKeyQuery$data | undefined;
	personalSpaceKeyLoading: boolean;
	personalSpaceKeyError: ApolloErrorInterop | undefined;
} => {
	const { userId, isLoggedIn } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isLicensed = useIsLicensed();
	const {
		data: nameData,
		loading: nameLoading,
		error: nameError,
	} = useQueryInterop<
		useCreatePersonalSpaceGetPublicNameQuery$data,
		useCreatePersonalSpaceGetPublicNameQuery$variables
	>(
		graphql`
			query useCreatePersonalSpaceGetPublicNameQuery($accountId: String!) {
				confluenceUsers(accountIds: [$accountId]) {
					nodes {
						__typename
						... on KnownUser {
							__typename
							publicName
						}
					}
				}
			}
		`,
		{
			variables: {
				accountId: userId || '',
			},
			skip: !isLicensed || !isLoggedIn,
		},
	);

	const {
		data: personalSpaceKeyData,
		loading: personalSpaceKeyLoading,
		error: personalSpaceKeyError,
	} = useQueryInterop<useCreatePersonalSpacepersonalSpaceKeyQuery$data>(personalSpaceKeyQuery, {
		skip: !isLoggedIn,
	});

	const [
		createPersonalSpaceMutation,
		{
			loading: createPersonalSpaceLoading,
			error: createPersonalSpaceError,
			data: personalSpaceData,
			called: createPersonalSpaceCalled,
		},
	] = useMutationInterop<
		useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$data,
		useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$variables
	>(
		graphql`
			mutation useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation(
				$name: String!
				$initialPermissionOption: InitialPermissionOptions
				$copySpacePermissionsFromSpaceKey: String
			) {
				createPersonalSpace(
					input: {
						spaceName: $name
						initialPermissionOption: $initialPermissionOption
						copySpacePermissionsFromSpaceKey: $copySpacePermissionsFromSpaceKey
					}
				) {
					id
					key
					name
					icon {
						path
					}
					homepageId
				}
			}
		`,
		{
			// After a successful mutation, fire-and-forget a re-fetch of the personal space
			// key query so the Apollo/Relay cache is warm for any subsequent consumer
			// (e.g. home page components that mount shortly after the onboarding redirect).
			// We intentionally do NOT set awaitRefetchQueries here: the primary fix for the
			// landing-destination timing bug is in NthUserOnboarding.tsx, which now reads
			// the key directly from the mutation result rather than from this cached query.
			// We pass the shared GraphQL tagged node (not a string query name): the Relay
			// variant of useMutationInterop rejects string refetchQueries entries at runtime.
			// Experiment: cc_ce_onboarding_personal_space_key_refetch
			...(expValEquals('cc_ce_onboarding_personal_space_key_refetch', 'isEnabled', true)
				? { refetchQueries: [{ query: personalSpaceKeyQuery }] }
				: {}),
			onCompleted: () => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'created',
						actionSubject: 'personalSpace',
						source,
					},
				}).fire();
			},
			onError: (err) => {
				if (isPersonalSpaceExistsError(err) && !isErrorMarkedAsHandled(err)) {
					markErrorAsHandled(err);
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'duplicated',
							actionSubject: 'personalSpaceAttempt',
							source,
							attributes: {
								error: err?.message,
								hasPublicSpace,
								isFoundingAdmin,
								personalSpaceKeyData: JSON.stringify(personalSpaceKeyData),
								personalSpaceKey: personalSpaceKeyData?.user?.confluence?.space?.key,
							},
						},
					}).fire();
					return;
				}

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'personalSpace',
						source,
						attributes: {
							error: err?.message,
						},
					},
				}).fire();
				markErrorAsHandled(err);
			},
		},
	);

	const confluenceUser = nameData?.confluenceUsers?.nodes?.[0];
	const publicName =
		confluenceUser?.__typename === 'KnownUser' ? confluenceUser.publicName : undefined;

	const createPersonalSpace = useCallback(():
		| Promise<void>
		| Promise<{
				data?: useCreatePersonalSpaceOnboardingCreatePersonalSpaceMutation$data | undefined;
		  }> => {
		if (publicName) {
			return createPersonalSpaceMutation({
				variables: {
					name: publicName,
					initialPermissionOption: InitialPermissionOptions.DEFAULT,
					copySpacePermissionsFromSpaceKey: undefined,
				},
			});
		}

		return Promise.resolve();
	}, [publicName, createPersonalSpaceMutation]);

	return {
		createPersonalSpace,
		queryLoading: nameLoading || personalSpaceKeyLoading,
		mutationLoading: createPersonalSpaceLoading,
		error: nameError || createPersonalSpaceError,
		personalSpaceData,
		createPersonalSpaceCalled,
		personalSpaceKeyData,
		personalSpaceKeyLoading,
		personalSpaceKeyError,
	};
};
